import React from "react";
import { useInView } from "react-intersection-observer";
import { FaApple } from "react-icons/fa";
import { useDownload } from "../DownloadWindow/DownloadContext";
import { gaEvent } from "../../utils/gaAnalytics";
import { MAC_INSTALLER_URL, MAC_FILENAME } from "./macDownload";
import {
  MAC_BUILD_LIVE,
  MAC_COMING_SOON_LABEL,
} from "../../hooks/useSmartDownload";

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12l5 5L20 7"
      stroke="#16A34A"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
      stroke="#16A34A"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="#16A34A"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrustedSection = () => {
  const { ref: textRef, inView: textView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const { ref: cardRef, inView: cardView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const {
    progress,
    downloadedMB,
    totalMB,
    isDownloading,
    isComplete,
    error,
    startDownload,
  } = useDownload();

  const handleMacDownload = (location) => {
    gaEvent("download_click", {
      platform: "mac",
      file_name: MAC_FILENAME,
      location,
    });
    startDownload(MAC_INSTALLER_URL, MAC_FILENAME, "mac");
  };

  let statusText;
  if (!MAC_BUILD_LIVE) statusText = MAC_COMING_SOON_LABEL;
  else if (error && !isDownloading) statusText = "Download started in browser";
  else if (isComplete) statusText = "Download complete";
  else if (isDownloading) statusText = `Downloading… ${Math.round(progress)}%`;
  else statusText = "Ready to download";

  const sizeText = totalMB
    ? `${downloadedMB} / ${totalMB} MB`
    : isDownloading
      ? `${downloadedMB} MB`
      : " ";

  return (
    <section className="w-full max-w-[1180px] mx-auto md:px-7 px-4 flex md:flex-row flex-col items-center md:items-start gap-10">
      <div
        ref={textRef}
        className={`${textView ? "animate-slideRight z-[3] anim-preserve" : "opacity-0 z-0"
          } flex-1 flex flex-col gap-4 items-center md:items-start text-center md:text-left`}
      >
        <h2 className="font-jakarta font-extrabold text-[#0F1B2D] text-[2.625rem] md:text-[2.4rem] lg:text-[2.8rem] xl:text-[3.125rem] leading-[1.24] tracking-[-0.02em]">
          Simple Download.{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]">
            Built For Mac
          </span>
        </h2>
        <p className="font-inter font-normal text-[#33425A] text-[1.313rem] md:text-[1.15rem] lg:text-[1.2rem] xl:text-[1.438rem] leading-[1.5] max-w-[520px]">
          Download Zero Typing directly to your Mac and start speaking
          instantly. Your voice becomes clean, ready-to-use text across the apps
          you already use.
        </p>

        <button
          type="button"
          disabled={!MAC_BUILD_LIVE}
          aria-disabled={!MAC_BUILD_LIVE}
          onClick={() => handleMacDownload("trusted_section")}
          style={{
            background: "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)",
          }}
          className={`hidden md:inline-flex self-center md:self-start w-max mt-1 ${
            MAC_BUILD_LIVE ? "hover:opacity-90 cursor-pointer" : "cursor-not-allowed"
          } transition-opacity duration-300 text-white rounded-full items-center gap-2.5 pl-2 pr-6 py-2 font-inter font-semibold text-base shadow-md`}
        >
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white shrink-0">
            <FaApple className="w-4 h-4 text-[#3AB1F6]" />
          </span>
          {MAC_COMING_SOON_LABEL}
        </button>
        <p className="hidden md:block font-inter text-[#8593A8] text-sm">
          Fast setup. Secure download. Ready in minutes.
        </p>

        <div className="hidden md:block border-t border-[#0000000D] pt-4 mt-2 max-w-[520px]">
          <p className="font-inter font-semibold text-[#0F1B2D] text-[0.95rem]">
            Made for Mac. Ready when you are.
          </p>
          <p className="font-inter font-normal text-[#33425A] text-[0.9375rem] leading-[1.55] mt-1">
            Zero Typing fits naturally into your Mac workflow, helping you write
            faster without changing the way you work.
          </p>
        </div>
      </div>

      <div
        ref={cardRef}
        className={`${cardView ? "animate-slideLeft z-[3] anim-preserve" : "opacity-0 z-0"
          } flex-1 w-full`}
      >
        <div
          role={MAC_BUILD_LIVE ? "button" : undefined}
          tabIndex={MAC_BUILD_LIVE ? 0 : undefined}
          aria-disabled={!MAC_BUILD_LIVE}
          onClick={
            MAC_BUILD_LIVE
              ? () => !isDownloading && handleMacDownload("trusted_card")
              : undefined
          }
          onKeyDown={
            MAC_BUILD_LIVE
              ? (e) => {
                if ((e.key === "Enter" || e.key === " ") && !isDownloading) {
                  e.preventDefault();
                  handleMacDownload("trusted_card");
                }
              }
              : undefined
          }
          className={`bg-white rounded-2xl border border-[#0000000D] shadow-sm p-5 flex flex-col gap-4 transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3AB1F6] ${!MAC_BUILD_LIVE
              ? "cursor-not-allowed"
              : isDownloading
                ? "cursor-progress hover:shadow-md"
                : "cursor-pointer hover:shadow-md"
            }`}
        >
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL}/downloadLogo.png`}
              alt="Zero Typing"
              className="w-10 h-10 rounded-xl shrink-0 object-contain"
            />
            <div className="flex flex-col flex-1 min-w-0">
              <span className="font-inter font-semibold text-[#0F1B2D] text-[0.95rem] truncate">
                ZeroTyping.dmg
              </span>
              <span className="font-inter font-normal text-[#33425A] text-[0.8125rem]">
                Zero Typing Ltd · Notarized by Apple
              </span>
            </div>
            <ShieldIcon />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-[0.75rem] font-inter text-[#33425A]">
              <span>{statusText}</span>
              <span>{sizeText}</span>
            </div>
            <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-150 ease-out"
                style={{
                  width: `${progress}%`,
                  background:
                    "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)",
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {["Gatekeeper Approved", "Apple Notarized", "Apple Silicon Native"].map(
              (label, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1.5 bg-[#DCFCE7] text-[#16A34A] rounded-full px-3 py-1 text-[0.75rem] font-inter font-medium"
                >
                  <CheckIcon />
                  {label}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedSection;
